import { StandardEvents } from '@shopify/events';

class AlteireBuyNow extends HTMLElement {
  pending = false;
  selecting = false;
  selectionFailed = false;
  generation = 0;

  connectedCallback() {
    this.form = this.closest('form');
    this.productForm = this.closest('product-form-component');
    this.button = this.querySelector('button');
    this.error = this.querySelector('[role="alert"]');
    if (!this.form || !this.button) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.button.addEventListener('click', this.checkout, { signal });
    this.form.addEventListener('change', this.sync, { signal });
    this.closest('product-card, dialog, .shopify-section')?.addEventListener(
      StandardEvents.productSelect, this.onSelection, { signal }
    );
    window.addEventListener('pageshow', this.onPageShow, { signal });
    this.observer = new MutationObserver(this.sync);
    this.observer.observe(this.form, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'value']
    });
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  onPageShow = () => {
    this.pending = false;
    this.sync();
  };

  sync = () => {
    const source = this.form?.querySelector('button[name="add"]');
    if (!this.button || !source) return;
    const variantId = this.form.elements.namedItem('id')?.value;
    const disabled = this.pending || this.selecting || this.selectionFailed ||
      source.disabled || source.getAttribute('aria-disabled') === 'true' || !variantId;
    if (this.button.disabled !== disabled) this.button.disabled = disabled;
    const label = this.pending ? this.dataset.loadingLabel : this.dataset.label;
    if (this.button.textContent.trim() !== label) this.button.textContent = label;
    this.button.setAttribute('aria-busy', String(this.pending || this.selecting));
    const style = getComputedStyle(source);
    for (const property of ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
      'letterSpacing', 'lineHeight', 'textTransform', 'borderRadius']) {
      if (this.button.style[property] !== style[property]) this.button.style[property] = style[property];
    }
  };

  onSelection = async (event) => {
    const card = event.target.closest?.('product-card');
    if (card && !card.contains(this)) return;
    const generation = ++this.generation;
    this.selecting = true;
    this.selectionFailed = false;
    this.error.hidden = true;
    this.sync();
    try {
      await event.promise;
    } catch {
      if (generation === this.generation) this.selectionFailed = true;
    } finally {
      if (generation === this.generation) {
        this.selecting = false;
        queueMicrotask(this.sync);
      }
    }
  };

  checkout = async () => {
    this.sync();
    if (this.button.disabled || !this.form.reportValidity()) return;
    const data = new FormData(this.form);
    if (!data.get('id')) return;
    if (!data.get('quantity')) data.set('quantity', this.productForm?.dataset.quantityDefault || '1');
    data.delete('sections');
    data.delete('sections_url');
    const quantitySelector = this.productForm?.querySelector('quantity-selector-component');
    const validation = quantitySelector?.canAddToCart?.();
    if (validation && !validation.canAdd) {
      this.showError(this.dataset.errorMessage);
      return;
    }
    this.pending = true;
    this.error.hidden = true;
    this.sync();
    try {
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json' }, body: data
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(typeof result.description === 'string' ? result.description : this.dataset.errorMessage);
      }
      window.location.assign(this.dataset.checkoutUrl);
    } catch (error) {
      this.pending = false;
      this.showError(error instanceof TypeError ? this.dataset.errorMessage : error.message);
      this.sync();
    }
  };

  showError(message) {
    this.error.textContent = message || this.dataset.errorMessage;
    this.error.hidden = false;
  }
}

if (!customElements.get('alteire-buy-now')) {
  customElements.define('alteire-buy-now', AlteireBuyNow);
}
